import { useDispatch, useSelector } from "react-redux";
import { Plugs, WarningCircle } from "@phosphor-icons/react";
import {
  selectClinicId,
  selectClinicProfile,
  setClinicProfile,
} from "../../store/slices/authSlice";
import {
  useDisconnectGrowMutation,
  useGetActiveGrowLinksQuery,
} from "../../store/services/growApi";
import { useToast } from "../../contexts/ToastContext";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./GrowIntegration.css";

/**
 * GrowDisconnectModal — confirm + run `disconnectGrow`.
 * Shows how many ACTIVE links (created/sent) will be cancelled at Grow.
 * Error code: CK-3868
 */
export default function GrowDisconnectModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const { showSuccess, showError } = useToast();
  const [disconnectGrow, { isLoading }] = useDisconnectGrowMutation();
  const { data: activeLinks, isLoading: countLoading } = useGetActiveGrowLinksQuery(
    { clinicId },
    { skip: !clinicId || !isOpen }
  );

  const activeCount = activeLinks?.length ?? 0;

  const confirm = async () => {
    const res = await disconnectGrow({ clinicId });
    if (res.error) {
      console.error("[CK-3868] Grow disconnect failed", {
        errorCode: "CK-3868",
        errorMessage: res.error.message,
        timestamp: new Date().toISOString(),
      });
      showError(res.error.message);
      return;
    }
    dispatch(setClinicProfile({ ...clinicProfile, isGrowIntegrated: false, growIntegration: null }));
    const { deactivated = 0, failed = 0 } = res.data || {};
    showSuccess(
      failed > 0
        ? `Grow נותק. ${deactivated} קישורים בוטלו, ${failed} לא בוטלו ב-Grow — בדקו בממשק Grow.`
        : "Grow נותק מהקליניקה"
    );
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="grow-overlay" onClick={onClose} role="presentation">
      <div className="grow-modal grow-modal-sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="grow-disc-title">
        <div className="grow-modal-header">
          <div className="grow-modal-title">
            <span className="grow-modal-icon danger"><Plugs size={20} weight="fill" /></span>
            <div>
              <span className="grow-eyebrow">Grow</span>
              <h3 id="grow-disc-title">ניתוק החיבור ל-Grow</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="grow-modal-body">
          <p>לאחר הניתוק לא ניתן יהיה לשלוח דרישות תשלום חדשות דרך Grow. תשלומים שכבר התקבלו נשארים רשומים.</p>
          <div className="grow-callout warn">
            <WarningCircle size={18} weight="fill" />
            {countLoading ? (
              <span>בודק קישורים פעילים…</span>
            ) : activeCount > 0 ? (
              <span>
                {activeCount === 1 ? "קישור תשלום אחד פעיל" : `${activeCount} קישורי תשלום פעילים`} יבוטלו ב-Grow ולא יהיה ניתן לשלם דרכם.
              </span>
            ) : (
              <span>אין קישורי תשלום פעילים — אפשר לנתק בבטחה.</span>
            )}
          </div>
          <p className="grow-note">אפשר להתחבר מחדש בכל עת עם אותם מזהים.</p>
        </div>

        <div className="grow-modal-footer">
          <div className="grow-footer-actions">
            <button type="button" className="grow-btn secondary" onClick={onClose} disabled={isLoading}>ביטול</button>
            <button type="button" className="grow-btn danger" onClick={confirm} disabled={isLoading}>
              {isLoading ? <LoaderLoop size="xs" /> : <Plugs size={16} />}
              ניתוק מ-Grow
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
